import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { Chip } from './Chip.jsx';
import { kioskApi } from '../lib/kiosk-api.js';

// Staff-facing recipe detail. Designed for a barista standing 1-2 feet
// from the iPad while making the drink — large type, clear measurements,
// step-by-step "follow along" cards with optional tap-to-check so they
// keep their place. State (checked steps) is per-session only; closing
// or switching recipes resets.
export function KioskRecipeDetail({ session, id, onBack }) {
  const locKey = session.location.key;
  const [recipe, setRecipe] = useState(null);
  const [err, setErr] = useState('');
  const [checkedSteps, setCheckedSteps] = useState(() => new Set());

  useEffect(() => {
    kioskApi('GET', `/api/kiosk/${locKey}/recipes/${id}`, null, session.token)
      .then(r => { setRecipe(r.recipe); setCheckedSteps(new Set()); })
      .catch(e => setErr(e.message || String(e)));
  }, [id]);

  function toggleStep(idx) {
    setCheckedSteps(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  }

  if (err) return <div style={{ background: '#fee', padding: 12, borderRadius: 8, color: '#b91c1c', fontSize: 13 }}>{err}</div>;
  if (!recipe) return <div style={{ fontSize: 13, color: '#9e9e9e' }}>Loading…</div>;

  const totalSteps = recipe.steps?.length || 0;
  const doneSteps = checkedSteps.size;

  // Pretty unit normalization for the ingredient table — "fl oz", "g",
  // "tsp", etc. Falls back to whatever the recipe stored.
  function fmtUnit(u) { return u ? String(u).trim() : ''; }
  function fmtQty(q) {
    if (q == null || q === '') return '';
    const n = Number(q);
    if (Number.isFinite(n)) return n.toString();
    return String(q);
  }

  return (
    <div style={{ paddingBottom: 32 }}>
      <button onClick={onBack}
        style={{ background: 'transparent', border: 'none', color: 'var(--forest)',
                  fontSize: 14, fontWeight: 600, cursor: 'pointer',
                  padding: '4px 0 14px', fontFamily: 'inherit' }}>
        ← Back to recipes
      </button>

      {/* Hero */}
      <div className="card" style={{ marginBottom: 14, overflow: 'hidden', padding: 0 }}>
        {recipe.photo_url && (
          <div style={{ width: '100%', height: 200, backgroundImage: `url(${recipe.photo_url})`,
                         backgroundSize: 'cover', backgroundPosition: 'center',
                         backgroundColor: '#f0ece4' }} />
        )}
        <div style={{ padding: 18 }}>
          <div style={{ fontSize: 12, color: 'var(--tea-gold, #b48a47)',
                         textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700 }}>
            {recipe.category || '—'}
          </div>
          <div style={{ fontSize: 28, fontWeight: 800, color: '#212121', marginTop: 4, lineHeight: 1.15 }}>
            {recipe.name}
          </div>
          {/* Quick-glance chips: prep time, yield, kind */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
            {recipe.prep_time_min && (
              <Chip icon="⏱" label={`${recipe.prep_time_min} min`} />
            )}
            {recipe.batch_yield && (
              <Chip icon="🥤" label={`Yields ${recipe.batch_yield}`} />
            )}
            {recipe.preparation_yield_quantity && recipe.preparation_yield_unit && (
              <Chip icon="📏" label={`${recipe.preparation_yield_quantity} ${recipe.preparation_yield_unit}`} />
            )}
            {recipe.difficulty && (
              <Chip icon="•" label={`Difficulty ${recipe.difficulty}`} />
            )}
          </div>
          {/* Allergen warning — bright orange so it's hard to miss */}
          {recipe.allergens?.length > 0 && (
            <div style={{ marginTop: 12, padding: '10px 14px',
                           background: '#fff7ed', border: '1px solid #fdba74',
                           borderRadius: 8, display: 'flex', gap: 10, alignItems: 'center' }}>
              <span style={{ fontSize: 18 }}>⚠️</span>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#9a3412',
                               textTransform: 'uppercase', letterSpacing: '0.05em' }}>Allergens</div>
                <div style={{ fontSize: 14, color: '#7c2d12', fontWeight: 600 }}>
                  {recipe.allergens.join(' · ')}
                </div>
              </div>
            </div>
          )}
          {recipe.notes && (
            <div style={{ marginTop: 14, padding: 12, background: '#f9fafb',
                           border: '1px solid #e5e7eb', borderRadius: 8,
                           fontSize: 14, color: '#374151', lineHeight: 1.55,
                           whiteSpace: 'pre-line' }}>
              {recipe.notes}
            </div>
          )}
        </div>
      </div>

      {/* Ingredients — 2-column on wide screens, single column on phone.
          Quantity + unit are tabular-aligned so the eye scans straight
          down the measurements. */}
      {recipe.ingredients?.length > 0 && (
        <div className="card" style={{ marginBottom: 14, padding: 18 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#212121' }}>Ingredients</div>
            <div style={{ fontSize: 11, color: '#9e9e9e' }}>{recipe.ingredients.length} item{recipe.ingredients.length === 1 ? '' : 's'}</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 0 }}>
            {recipe.ingredients.map((ing, i) => {
              const qty = fmtQty(ing.quantity);
              const unit = fmtUnit(ing.unit);
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 12,
                                       padding: '10px 0', borderBottom: '1px solid #f3f4f6' }}>
                  <div style={{ flexShrink: 0, minWidth: 90, textAlign: 'right',
                                 fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                 fontSize: 16, fontWeight: 700, color: '#1f2a44',
                                 lineHeight: 1.3 }}>
                    {qty || '—'}
                    {unit && <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 500, marginLeft: 4 }}>{unit}</span>}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: '#212121', lineHeight: 1.35 }}>
                      {ing.name}
                    </div>
                    {/* Was ing.notes — recipe_ingredients has no such column
                        and never did, so this rendered nothing even once the
                        500 was fixed. is_allergen is the flag that actually
                        exists, and it is the one worth showing on a bar iPad. */}
                    {ing.is_allergen && (
                      <div style={{ fontSize: 12, color: '#b45309', marginTop: 2, lineHeight: 1.4, fontWeight: 600 }}>
                        allergen
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Steps — each is a big card with a numbered circle. Tap the circle
          (or the card) to mark complete; ✓ replaces the number, the card
          dims slightly so the next un-done step is the obvious focal
          point. Counter at the top of the section shows progress. */}
      {totalSteps > 0 && (
        <div>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', margin: '0 4px 10px' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#212121' }}>Steps</div>
            <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>
              {doneSteps} of {totalSteps} complete
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {recipe.steps.map((s, i) => {
              const done = checkedSteps.has(i);
              return (
                <button key={i} onClick={() => toggleStep(i)}
                  style={{ background: done ? '#f0fdf4' : '#fff',
                            border: `1px solid ${done ? '#bbf7d0' : '#e5e7eb'}`,
                            borderRadius: 12, padding: 14,
                            display: 'flex', gap: 14, alignItems: 'flex-start',
                            cursor: 'pointer', textAlign: 'left',
                            fontFamily: 'inherit', width: '100%',
                            opacity: done ? 0.65 : 1,
                            transition: 'opacity 0.15s, background 0.15s' }}>
                  <div style={{ width: 40, height: 40, borderRadius: '50%',
                                 background: done ? '#16a34a' : 'var(--ink, #1f2a44)',
                                 color: '#fff', display: 'flex',
                                 alignItems: 'center', justifyContent: 'center',
                                 fontSize: done ? 22 : 18, fontWeight: 700,
                                 flexShrink: 0, lineHeight: 1 }}>
                    {done ? '✓' : (i + 1)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, color: '#212121', lineHeight: 1.55,
                                   textDecoration: done ? 'line-through' : 'none' }}>
                      {/* recipe_steps stores its text as `instruction`; this
                          read s.description, so every step rendered blank. */}
                      {s.instruction}
                    </div>
                    {s.image_url && (
                      <img src={s.image_url} alt={`Step ${i + 1}`}
                           style={{ width: '100%', maxWidth: 480, marginTop: 10,
                                     borderRadius: 8, display: 'block' }} />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
          {doneSteps === totalSteps && totalSteps > 0 && (
            <div style={{ marginTop: 14, padding: 14, background: '#f0fdf4',
                           border: '1px solid #bbf7d0', borderRadius: 10,
                           textAlign: 'center', fontSize: 14, color: '#166534', fontWeight: 600 }}>
              ✓ All steps complete — drink ready
            </div>
          )}
        </div>
      )}
    </div>
  );
}
